import {
  computed,
  inject,
  type ComputedRef,
  type InjectionKey,
} from 'vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {Session} from '@/ts/types/dto/interventions-dto';
import {useWeightTrackingStore} from '@/ts/store/weight-tracking.store';
import {useSessionId} from '@/ts/composables/stateful/use-session';

/**
 * Injection key for the current session id. Provided by `session-cms-content`
 * so deeply nested weight inputs / the weight-loss display can scope themselves
 * to the active session without prop drilling. Resolves to `null` outside a
 * session (e.g. homepage questionnaires) — in which case no pre-fill happens.
 */
const CURRENT_SESSION_ID: InjectionKey<ComputedRef<UuidDTO | null>> = Symbol('currentSessionId');

const NO_SESSION: ComputedRef<UuidDTO | null> = computed(() => null);

/** Result of {@link useSessionWeight}. */
interface UseSessionWeightRes {
  /** Records the weight (grams) the user just entered for a question. */
  recordWeight(contentId: UuidDTO, grams: number): void;
  /** Pre-fill value (grams) for an unanswered weight question, scoped to this run of the session. */
  getPrefill(contentId: UuidDTO): number | null;
  /** Most recent weight (grams) entered in this run, for the weight-loss % display. */
  latestWeight: ComputedRef<number | null>;
}

/**
 * Finds an already-answered weight (grams) within a session, scanning page
 * components in document order and keeping the most recent match. When
 * `contentId` is provided it matches that specific question; otherwise it
 * returns the latest answered weight question of any id (for the % display).
 *
 * Matches by `numeric.format === 'weight'`, NOT by questionnaire type: the first
 * weight a user enters in a session is the CDC enrollment question, which lives
 * in a STANDARD questionnaire — gating on WEIGHT_LOSS here would miss it and
 * break the within-session pre-fill. Conflating distinct weight questions is
 * prevented by the `contentId` match in the keyed (getPrefill) path.
 *
 * Known limitation (pre-existing, also in read-only-component.vue): does not
 * descend into nested layout components (`component.componentLayout`); a
 * WEIGHT_LOSS questionnaire authored inside a Layout would be missed on the
 * reload fallback. Not reachable with current CDC content, where weight
 * questionnaires are top-level page components.
 */
function findAnsweredWeight(session: Session | null, contentId: UuidDTO | null): number | null {
  if (!session) {
    return null;
  }
  let found: number | null = null;
  for (const page of session.pages) {
    for (const component of page.components) {
      for (const question of component.questionnaire?.questions ?? []) {
        const isWeight = question.numeric?.format === 'weight';
        const isAnswered = question.numericSelected !== null && question.numericSelected !== void 0;
        if (isWeight && isAnswered && (contentId === null || question.contentId === contentId)) {
          found = question.numericSelected;
        }
      }
    }
  }
  return found;
}

/**
 * Weight helper for one run of one session: pre-fills a weight field from the value already
 * entered for the same question earlier in that run (live, or derived from the server answer
 * after a reload). Nothing carries across sessions, and nothing carries across enrollments —
 * the same session taken twice is two runs, and the live cache is keyed accordingly (IPD-3030).
 */
function useSessionWeight(): UseSessionWeightRes {
  const sessionIdRef = inject(CURRENT_SESSION_ID, NO_SESSION);
  const store = useWeightTrackingStore();
  const {session} = useSessionId(sessionIdRef);

  // What the LIVE cache is keyed on: the user's row for this session, not the route id it is
  // looked up by. That one names the content and is the same every time the intervention is
  // taken, so leaving an intervention and starting it again kept the cache alive and pre-filled
  // the weight typed in the run the user had abandoned (IPD-3030).
  //
  // Guarded on the injected id first: outside a session there is no intervention to resolve, and
  // reaching for one there throws (a weight input can render on its own, as its test does).
  //
  // Absent — a backend that does not serve the field yet — falls back to the route id, which is
  // what this keyed on before. That reinstates the cross-enrollment bug on those backends and
  // nothing else; keying on nothing instead would be worse than the bug it fixes. A weight
  // questionnaire renders through `questionnaire-grid-vertical`, which emits no update, so the
  // typed value is not persisted until Continue and lives only in this cache meanwhile: with no
  // key there is nothing to record and nothing to read back, `latestWeight` resolves to null, and
  // `weight-loss-calculation-display` — which the question components pass no `currentWeight` —
  // drops the whole goal block. The fix switches itself on the moment the field arrives.
  const cacheKey = computed<UuidDTO | null>(
    () => (sessionIdRef.value === null ? null : session.value?.userSessionId ?? sessionIdRef.value),
  );

  function recordWeight(contentId: UuidDTO, grams: number): void {
    const key = cacheKey.value;
    if (key !== null) {
      store.setWeight(key, contentId, grams);
    }
  }

  function getPrefill(contentId: UuidDTO): number | null {
    if (sessionIdRef.value === null) {
      return null;
    }
    const key = cacheKey.value;
    const live = key === null ? null : store.getWeight(key, contentId);
    return live ?? findAnsweredWeight(session.value, contentId);
  }

  const latestWeight = computed<number | null>(() => {
    if (sessionIdRef.value === null) {
      return null;
    }
    const key = cacheKey.value;
    const live = key === null ? null : store.getLatestWeight(key);
    return live ?? findAnsweredWeight(session.value, null);
  });

  return {recordWeight, getPrefill, latestWeight};
}

export {CURRENT_SESSION_ID, useSessionWeight};
export type {UseSessionWeightRes};
